// CONTRACT:C4-DESIGN-TOKENS.1.0
/** Core token operations:
 *   - buildDesignTokens()   — the grouped DesignTokens object whose leaves are CssVar
 *                             references (e.g. "var(--status-hostile)"). Components consume
 *                             THESE (semantic CSS variables), never raw primitive values.
 *   - toCssVariables(theme) — primitive→CSS-variable emitter: { "--color-surface-base": "#0B0F14", ... }.
 *   - resolveToken(theme,k) — accessor: returns the primitive value for a semantic key in a
 *                             theme, throwing ErrMissingToken (with documented safe default available). */

import type { DesignTokens, CssVar, Theme } from "../../contracts-ts/src/index.ts";
import { SLOT_KEYS, STATUS_KEYS } from "./slots.ts";
import { ErrMissingToken } from "./errors.ts";
import { assertTokenType } from "./validate.ts";
import { safeDefaultFor } from "./defaults.ts";

/** A semantic key → its CSS-variable reference, e.g. "status-hostile" → "var(--status-hostile)". */
export function cssVar(key: string): CssVar {
  return `var(--${key})`;
}

/** The grouped DesignTokens object (per the CONTRACT:C4 interface). Every leaf is a CssVar
 *  reference; this is THEME-INDEPENDENT (it points at variables, not values), so a runtime
 *  theme swap re-points the variables without rebuilding this object. */
export function buildDesignTokens(): DesignTokens {
  return {
    color: {
      surface: {
        base: cssVar("color-surface-base"),
        raised: cssVar("color-surface-raised"),
        overlay: cssVar("color-surface-overlay"),
        sunken: cssVar("color-surface-sunken"),
      },
      interactive: {
        default: cssVar("color-interactive-default"),
        hover: cssVar("color-interactive-hover"),
        active: cssVar("color-interactive-active"),
        disabled: cssVar("color-interactive-disabled"),
      },
      // Semantic verdict/state hues (policy-outcome chrome; never a map marker).
      semantic: {
        grant: cssVar("color-semantic-grant"),
        deny: cssVar("color-semantic-deny"),
        caution: cssVar("color-semantic-caution"),
      },
    },
    status: {
      hostile: cssVar("status-hostile"),
      friendly: cssVar("status-friendly"),
      neutral: cssVar("status-neutral"),
      unknown: cssVar("status-unknown"),
      pending: cssVar("status-pending"),
    },
    typography: {
      scale: {
        xs: cssVar("typography-scale-xs"),
        sm: cssVar("typography-scale-sm"),
        md: cssVar("typography-scale-md"),
        lg: cssVar("typography-scale-lg"),
        xl: cssVar("typography-scale-xl"),
      },
      family: {
        sans: cssVar("typography-family-sans"),
        mono: cssVar("typography-family-mono"),
        // The HUD / display face (Saira) — the canon's third family slot,
        // alongside sans (prose/UI) and mono (envelope/coordinates/crypto).
        hud: cssVar("typography-family-hud"),
      },
    },
    spacing: {
      xs: cssVar("spacing-xs"),
      sm: cssVar("spacing-sm"),
      md: cssVar("spacing-md"),
      lg: cssVar("spacing-lg"),
      xl: cssVar("spacing-xl"),
    },
    radius: {
      chip: cssVar("radius-chip"),
      control: cssVar("radius-control"),
      panel: cssVar("radius-panel"),
    },
    elevation: {
      flat: cssVar("elevation-flat"),
      raised: cssVar("elevation-raised"),
      overlay: cssVar("elevation-overlay"),
    },
    motion: {
      duration: {
        instant: cssVar("motion-duration-instant"),
        fast: cssVar("motion-duration-fast"),
        slow: cssVar("motion-duration-slow"),
      },
      easing: {
        standard: cssVar("motion-easing-standard"),
        decelerate: cssVar("motion-easing-decelerate"),
      },
    },
  };
}

/** The frozen, shared semantic token object (the vocabulary every consumer imports). */
export const tokens: DesignTokens = buildDesignTokens();

/** Options for resolveToken. */
export interface ResolveOptions {
  /** When true, a missing token returns the documented safe default instead of throwing.
   *  The resolver NEVER yields transparent/undefined output — this is the fail-closed path. */
  readonly fallback?: boolean;
}

/** Resolve a semantic token key to its PRIMITIVE value in a given theme.
 *  - Present + correct type → the value.
 *  - Missing → throws ErrMissingToken (or, with { fallback: true }, the documented safe default).
 *  - Present but wrong type → throws ErrTokenTypeMismatch (never coerced silently). */
export function resolveToken(theme: Theme, key: string, opts: ResolveOptions = {}): string {
  const raw = (theme.tokens as Record<string, unknown>)?.[key];
  if (raw === undefined) {
    if (opts.fallback) return safeDefaultFor(key);
    throw new ErrMissingToken(key, theme?.id ?? "<unknown>");
  }
  // Guard against silently coercing a wrong-typed value (e.g. number where a color is required).
  assertTokenType(key, raw);
  return raw as string;
}

/** Emit a theme as a flat Record of CSS custom properties: { "--<key>": "<primitive>" }.
 *  This is the runtime-consumption target — set on :root to (re)skin every component and
 *  deck.gl layer with NO recompile. Only declared semantic slots are emitted. */
export function toCssVariables(theme: Theme): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of SLOT_KEYS) {
    const raw = (theme.tokens as Record<string, unknown>)?.[key];
    if (raw === undefined) {
      // Fail closed: emit the documented safe default rather than dropping the variable.
      out[`--${key}`] = safeDefaultFor(key);
      continue;
    }
    assertTokenType(key, raw);
    out[`--${key}`] = raw as string;
  }
  return out;
}

/** Resolve an affiliation/status slot's primitive color in a theme (single source of
 *  affiliation color). Throws ErrMissingToken if absent (unless fallback). */
export function resolveStatusColor(
  theme: Theme,
  statusKey: (typeof STATUS_KEYS)[number],
  opts: ResolveOptions = {},
): string {
  return resolveToken(theme, statusKey, opts);
}
